import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import "core-js/modules/web.dom.iterable";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import PropTypes from 'prop-types';
import Handle from './Handle';
import * as SliderConstants from './constants/slider';
import LinearAlgorithm from './algorithms/linear';
import getThemeClasses from '@oxygen/theme';
import styles from './Rheostat.module.scss';
import { concatString } from '../utils/string';

var getContainerClasses = pitPoints => {
  var classes = "o-cpnuEd o-efHQCX o-NBTwp o-brXWGL ".concat(styles.container, " ").concat(pitPoints.length ? "o-fzowbC" : '', "");
  return classes;
};

var getSliderClasses = disabled => {
  var classes = "o-bfyaNx o-fDmddo o-dAkHnV ".concat(disabled ? "o-crcSXU" : "o-fjzaYs", "");
  return classes;
};

var getProgressBarClasses = disabled => {
  var classes = "o-bXKmQE o-fDmddo o-dAkHnV ".concat(styles.progressBar, " ").concat(disabled ? "o-cbkKHo" : "o-jeeUx", "");
  return classes;
};

var getPitContainerClasses = disabled => {
  var classes = "o-bXKmQE o-KxopV o-bfyaNx ".concat(styles.pitContainer, " ").concat(disabled ? "o-ZjKyg" : "o-cpNAVm", "");
  return classes;
};

var getPitSubLabelClasses = disabled => {
  var classes = "o-cNwRuH ".concat(disabled ? "o-ZjKyg" : "o-cpNAVm", "");
  return classes;
};

var pitLabelClasses = "o-bXKmQE o-bVSleT";
var has = Object.prototype.hasOwnProperty;
var PropTypeArrOfNumber = process.env.NODE_ENV !== "production" ? PropTypes.arrayOf(PropTypes.number) : {};

function getHandleFor(event) {
  return Number(event.currentTarget.getAttribute('data-handle-key'));
}

function killEvent(event) {
  event.stopPropagation();

  if (event.cancelable) {
    event.preventDefault();
  }
}

var propTypes = process.env.NODE_ENV !== "production" ? {
  /** The algorithm, by default it will use linear algorithm. */
  algorithm: PropTypes.shape({
    getValue: PropTypes.func,
    getPosition: PropTypes.func
  }),

  /** Custom style classes for the component. */
  className: PropTypes.string,

  /** Prevent the slider from moving on interaction */
  disabled: PropTypes.bool,

  /** If true it will show tooltip on slider  */
  handleTooltip: PropTypes.bool,

  /** A custom method to format tooltip value */
  handleTooltipLabel: PropTypes.func,

  /** The maximum possible value */
  max: PropTypes.number,

  /** The minimum possible value */
  min: PropTypes.number,

  /** Called on click */
  onClick: PropTypes.func,

  /** Called whenever the user is done changing values on the slider */
  onChange: PropTypes.func,

  /** Called on key press */
  onKeyPress: PropTypes.func,

  /** Called when you finish dragging a handle */
  onSliderDragEnd: PropTypes.func,

  /** Called every time the slider is dragged and the value changes */
  onSliderDragMove: PropTypes.func,

  /** Called when you start dragging a handle */
  onSliderDragStart: PropTypes.func,

  /** Called whenever the user is actively changing the values on the slider (dragging, clicked, keypress) */
  onValuesUpdated: PropTypes.func,

  /** The points that pits are rendered on */
  pitPoints: PropTypeArrOfNumber,

  /** A custom function to format pit points */
  pitPointLabel: PropTypes.func,

  /** Should we snap, it controls the slider's snapping behavior */
  snap: PropTypes.bool,

  /** The points we should snap to */
  snapPoints: PropTypeArrOfNumber,

  /** Snap handle on drag move */
  snapOnDragMove: PropTypes.bool,

  /** Disable snap on click */
  disableSnapOnClick: PropTypes.bool,

  /** The values */
  values: PropTypeArrOfNumber,

  /** Node to add sublable for pitpoints */
  pitPointSubLabel: PropTypes.node,

  /** Value for data testing attribute. */
  'data-testing-id': PropTypes.string
} : {};
var defaultProps = {
  algorithm: LinearAlgorithm,
  children: null,
  disabled: false,
  handleTooltip: true,
  handleTooltipLabel: null,
  max: SliderConstants.PERCENT_FULL,
  min: SliderConstants.PERCENT_EMPTY,
  onClick: null,
  onChange: null,
  onKeyPress: null,
  onSliderDragEnd: null,
  onSliderDragMove: null,
  onSliderDragStart: null,
  onValuesUpdated: null,
  pitPoints: [],
  pitPointLabel: null,
  snap: false,
  snapPoints: [],
  snapOnDragMove: false,
  disableSnapOnClick: false,
  values: [SliderConstants.PERCENT_EMPTY],
  className: ''
};

class Rheostat extends React.PureComponent {
  constructor(_props) {
    super(_props);

    _defineProperty(this, "getPublicState", () => {
      var {
        min,
        max
      } = this.props;
      var {
        values
      } = this.state;
      return {
        max,
        min,
        values
      };
    });

    _defineProperty(this, "getSliderBoundingBox", () => {
      var rheostat = this.rheostat;
      var node = rheostat.getDOMNode ? rheostat.getDOMNode() : rheostat;
      var rect = node.getBoundingClientRect();
      return {
        height: rect.height || node.clientHeight,
        left: rect.left,
        top: rect.top,
        width: rect.width || node.clientWidth
      };
    });

    _defineProperty(this, "getProgressStyle", idx => {
      var {
        handlePos
      } = this.state;
      var value = handlePos[idx];

      if (idx === 0) {
        return {
          left: 0,
          width: "".concat(value, "%")
        };
      }

      var prevValue = handlePos[idx - 1];
      var diffValue = value - prevValue;
      return {
        left: "".concat(prevValue, "%"),
        width: "".concat(diffValue, "%")
      };
    });

    _defineProperty(this, "getMinValue", idx => {
      return this.state.values[idx - 1] ? Math.max(this.props.min, this.state.values[idx - 1]) : this.props.min;
    });

    _defineProperty(this, "getMaxValue", idx => {
      return this.state.values[idx + 1] ? Math.min(this.props.max, this.state.values[idx + 1]) : this.props.max;
    });

    _defineProperty(this, "getHandleDimensions", (event, sliderBox) => {
      var handleNode = event.currentTarget || null;
      if (!handleNode) return 0;
      return handleNode.clientWidth / sliderBox.width * SliderConstants.PERCENT_FULL / 2;
    });

    _defineProperty(this, "getClosestSnapPoint", value => {
      if (!this.props.snapPoints.length) return value;
      return this.props.snapPoints.reduce((snapTo, snap) => Math.abs(snapTo - value) < Math.abs(snap - value) ? snapTo : snap);
    });

    _defineProperty(this, "getSnapPosition", positionPercent => {
      if (!this.props.snap) return positionPercent;
      var {
        algorithm,
        max,
        min
      } = this.props;
      var value = algorithm.getValue(positionPercent, min, max);
      var snapValue = this.getClosestSnapPoint(value);
      return algorithm.getPosition(snapValue, min, max);
    });

    _defineProperty(this, "getNextPositionForKey", (idx, keyCode) => {
      var {
        handlePos,
        values
      } = this.state;
      var {
        algorithm,
        max,
        min,
        snapPoints
      } = this.props;
      var shouldSnap = this.props.snap;
      var proposedValue = values[idx];
      var proposedPercentage = handlePos[idx];
      var originalPercentage = proposedPercentage;
      var stepValue = 1;

      if (max >= 100) {
        proposedPercentage = Math.round(proposedPercentage);
      } else {
        stepValue = 100 / (max - min);
      }

      var currentIndex = null;

      if (shouldSnap) {
        currentIndex = snapPoints.indexOf(this.getClosestSnapPoint(values[idx]));
      }

      var stepMultiplier = {
        [SliderConstants.KEYS.LEFT]: v => v * -1,
        [SliderConstants.KEYS.RIGHT]: v => v * 1,
        [SliderConstants.KEYS.UP]: v => v * 1,
        [SliderConstants.KEYS.DOWN]: v => v * -1,
        [SliderConstants.KEYS.PAGE_DOWN]: v => v > 1 ? -v : v * -10,
        [SliderConstants.KEYS.PAGE_UP]: v => v > 1 ? v : v * 10
      };

      if (has.call(stepMultiplier, keyCode)) {
        proposedPercentage += stepMultiplier[keyCode](stepValue);

        if (shouldSnap) {
          if (proposedPercentage > originalPercentage) {
            // move cursor right unless overflow
            if (currentIndex < snapPoints.length - 1) {
              proposedValue = snapPoints[currentIndex + 1];
            } // move cursor left unless there is overflow

          } else if (currentIndex > 0) {
            proposedValue = snapPoints[currentIndex - 1];
          }
        }
      } else if (keyCode === SliderConstants.KEYS.HOME) {
        proposedPercentage = SliderConstants.PERCENT_EMPTY;

        if (shouldSnap) {
          proposedValue = snapPoints[0];
        }
      } else if (keyCode === SliderConstants.KEYS.END) {
        proposedPercentage = SliderConstants.PERCENT_FULL;

        if (shouldSnap) {
          proposedValue = snapPoints[snapPoints.length - 1];
        }
      } else {
        return null;
      }

      return shouldSnap ? algorithm.getPosition(proposedValue, min, max) : proposedPercentage;
    });

    _defineProperty(this, "getNextState", (idx, proposedPosition) => {
      var {
        handlePos
      } = this.state;
      var {
        max,
        min
      } = this.props;
      var actualPosition = this.validatePosition(idx, proposedPosition);
      var nextHandlePos = handlePos.length !== 0 ? handlePos.map((pos, index) => index === idx ? actualPosition : pos) : [proposedPosition];
      return {
        handlePos: nextHandlePos,
        values: nextHandlePos.map(pos => this.props.algorithm.getValue(pos, min, max))
      };
    });

    _defineProperty(this, "getClosestHandle", positionPercent => {
      var {
        handlePos
      } = this.state;
      return handlePos.reduce((closestIdx, node, idx) => {
        var challenger = Math.abs(handlePos[idx] - positionPercent);
        var current = Math.abs(handlePos[closestIdx] - positionPercent);
        return challenger < current ? idx : closestIdx;
      }, 0);
    });

    _defineProperty(this, "setStartSlide", (event, x, y) => {
      var sliderBox = this.getSliderBoundingBox();
      this.setState({
        handleDimensions: this.getHandleDimensions(event, sliderBox),
        sliderBox,
        slidingIndex: getHandleFor(event)
      });
    });

    _defineProperty(this, "setRef", ref => {
      this.rheostat = ref;
    });

    _defineProperty(this, "startMouseSlide", event => {
      this.setStartSlide(event, event.clientX, event.clientY);

      if (typeof document.addEventListener === 'function') {
        document.addEventListener('mousemove', this.handleMouseSlide, false);
        document.addEventListener('mouseup', this.endSlide, false);
      } else {
        document.attachEvent('onmousemove', this.handleMouseSlide);
        document.attachEvent('onmouseup', this.endSlide);
      }

      killEvent(event);
    });

    _defineProperty(this, "startTouchSlide", event => {
      if (event.changedTouches.length > 1) return;
      var touch = event.changedTouches[0];
      this.setStartSlide(event, touch.clientX, touch.clientY);
      document.addEventListener('touchmove', this.handleTouchSlide, false);
      document.addEventListener('touchend', this.endSlide, false);
      if (this.props.onSliderDragStart) this.props.onSliderDragStart(event);
      killEvent(event);
    });

    _defineProperty(this, "handleMouseSlide", event => {
      if (this.state.slidingIndex === null) return;
      this.handleSlide(event.clientX, event.clientY);
      killEvent(event);
    });

    _defineProperty(this, "handleTouchSlide", event => {
      if (this.state.slidingIndex === null) return;

      if (event.changedTouches.length > 1) {
        this.endSlide();
        return;
      }

      var touch = event.changedTouches[0];
      this.handleSlide(touch.clientX, touch.clientY);
      killEvent(event);
    });

    _defineProperty(this, "handleSlide", (x, y) => {
      var {
        slidingIndex: idx,
        sliderBox
      } = this.state;
      var positionPercent = (x - sliderBox.left) / sliderBox.width * SliderConstants.PERCENT_FULL;
      var handleId = this.getClosestHandle(positionPercent);

      if (this.props.snapOnDragMove) {
        var validPositionPercent = this.getSnapPosition(positionPercent);
        this.slideTo(handleId, validPositionPercent, () => this.fireChangeEvent());
      } else {
        this.slideTo(handleId, positionPercent);
      }

      if (this.canMove(idx, positionPercent)) {
        // update mouse positions
        this.setState({
          x,
          y
        });
        if (this.props.onSliderDragMove) this.props.onSliderDragMove(x, y);
      }
    });

    _defineProperty(this, "endSlide", event => {
      var idx = this.state.slidingIndex;
      this.setState({
        slidingIndex: null
      });

      if (typeof document.removeEventListener === 'function') {
        document.removeEventListener('mouseup', this.endSlide, false);
        document.removeEventListener('touchend', this.endSlide, false);
        document.removeEventListener('touchmove', this.handleTouchSlide, false);
        document.removeEventListener('mousemove', this.handleMouseSlide, false);
      } else {
        document.detachEvent('onmousemove', this.handleMouseSlide);
        document.detachEvent('onmouseup', this.endSlide);
      }

      if (this.props.onSliderDragEnd) this.props.onSliderDragEnd(event);

      if (this.props.snap) {
        var positionPercent = this.getSnapPosition(this.state.handlePos[idx]);
        this.slideTo(idx, positionPercent, () => this.fireChangeEvent());
      } else {
        this.fireChangeEvent();
      }

      killEvent(event);
    });

    _defineProperty(this, "handleClick", event => {
      if (event.target.getAttribute('data-handle-key')) {} //return
      // Calculate the position of the slider on the page so we can determine
      // the position where you click in relativity.


      var sliderBox = this.getSliderBoundingBox();
      var positionDecimal = (event.clientX - sliderBox.left) / sliderBox.width;
      var positionPercent = positionDecimal * SliderConstants.PERCENT_FULL;
      var handleId = this.getClosestHandle(positionPercent);

      if (positionPercent === this.state.handlePos[handleId]) {
        return;
      }

      if (this.props.disableSnapOnClick) {
        // Move the handle there
        this.slideTo(handleId, positionPercent, () => this.fireChangeEvent());
      } else {
        var validPositionPercent = this.getSnapPosition(positionPercent); // Move the handle there

        this.slideTo(handleId, validPositionPercent, () => this.fireChangeEvent());
      }

      if (this.props.onClick) this.props.onClick(event);
    });

    _defineProperty(this, "handleKeydown", event => {
      var idx = getHandleFor(event);

      if (event.keyCode === SliderConstants.KEYS.ESC) {
        event.currentTarget.blur();
        return;
      }

      var proposedPercentage = this.getNextPositionForKey(idx, event.keyCode);
      if (proposedPercentage === null) return;

      if (this.canMove(idx, proposedPercentage)) {
        this.slideTo(idx, proposedPercentage, () => this.fireChangeEvent());

        if (this.props.onKeyPress) {
          this.props.onKeyPress(event);
        }
      }

      killEvent(event);
    });

    _defineProperty(this, "validatePosition", (idx, proposedPosition) => {
      var {
        handlePos,
        handleDimensions
      } = this.state;
      return Math.max(Math.min(proposedPosition, handlePos[idx + 1] !== undefined ? handlePos[idx + 1] - handleDimensions : SliderConstants.PERCENT_FULL // 100% is the highest value
      ), handlePos[idx - 1] !== undefined ? handlePos[idx - 1] + handleDimensions : SliderConstants.PERCENT_EMPTY // 0% is the lowest value
      );
    });

    _defineProperty(this, "validateValues", (proposedValues, props) => {
      var {
        max,
        min
      } = props || this.props;
      return proposedValues.map((value, idx, values) => {
        var realValue = Math.max(Math.min(value, max), min);

        if (values.length && realValue < values[idx - 1]) {
          return values[idx - 1];
        }

        return realValue;
      });
    });

    _defineProperty(this, "canMove", (idx, proposedPosition) => {
      var {
        handlePos,
        handleDimensions
      } = this.state;
      if (proposedPosition < SliderConstants.PERCENT_EMPTY) return false;
      if (proposedPosition > SliderConstants.PERCENT_FULL) return false;
      var nextHandlePosition = handlePos[idx + 1] !== undefined ? handlePos[idx + 1] - handleDimensions : Infinity;
      if (proposedPosition > nextHandlePosition) return false;
      var prevHandlePosition = handlePos[idx - 1] !== undefined ? handlePos[idx - 1] + handleDimensions : -Infinity;
      if (proposedPosition < prevHandlePosition) return false;
      return true;
    });

    _defineProperty(this, "fireChangeEvent", () => {
      var {
        onChange
      } = this.props;
      if (onChange) onChange(this.getPublicState());
    });

    _defineProperty(this, "slideTo", (idx, proposedPosition, onAfterSet) => {
      var nextState = this.getNextState(idx, proposedPosition);
      this.setState(nextState, () => {
        var {
          onValuesUpdated
        } = this.props;
        if (onValuesUpdated) onValuesUpdated(this.getPublicState());
        if (onAfterSet) onAfterSet();
      });
    });

    _defineProperty(this, "updateNewValues", nextProps => {
      var {
        slidingIndex
      } = this.state; // Don't update while the slider is sliding

      if (slidingIndex !== null) {
        return;
      }

      var {
        max,
        min,
        values,
        algorithm
      } = nextProps;
      var nextValues = this.validateValues(values, nextProps);
      this.setState({
        handlePos: nextValues.map(value => algorithm.getPosition(value, min, max)),
        values: nextValues
      }, () => this.fireChangeEvent());
    });

    _defineProperty(this, "getTooltipLabel", (value, index) => {
      var {
        handleTooltipLabel
      } = this.props;

      if (handleTooltipLabel) {
        return handleTooltipLabel(value, index);
      }

      return value;
    });

    _defineProperty(this, "getPitPointsLabel", (value, index) => {
      var {
        pitPointLabel
      } = this.props;

      if (pitPointLabel) {
        return pitPointLabel(value, index);
      }

      return value;
    });

    var {
      algorithm: _algorithm,
      max: _max,
      min: _min,
      values: _values
    } = this.props;

    var valuesArrayExist = Array.isArray(_values) && _values.length;

    var handlePositions = valuesArrayExist ? _values.map(value => _algorithm.getPosition(value, _min, _max)) : [];
    this.state = {
      handlePos: handlePositions,
      handleDimensions: 0,
      sliderBox: {},
      slidingIndex: null,
      values: _values
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    var {
      disabled,
      min,
      max
    } = this.props;
    var {
      values,
      slidingIndex
    } = this.state;
    var minMaxChanged = nextProps.min !== min || nextProps.max !== max;
    var valuesChanged = values.length !== nextProps.values.length || values.some((value, idx) => nextProps.values[idx] !== value);
    var willBeDisabled = nextProps.disabled && !disabled;

    if (minMaxChanged || valuesChanged) {
      this.updateNewValues(nextProps);
    }

    if (willBeDisabled && slidingIndex !== null) {
      this.endSlide();
    }
  }

  render() {
    var _this$props = this.props,
        {
      algorithm,
      className,
      // added to omit from other
      'data-testing-id': testingName,
      disabled,
      disableSnapOnClick,
      // added to omit from other
      handleTooltip,
      handleTooltipLabel,
      // added to omit from other
      max,
      min,
      onClick,
      // added to omit from other
      onKeyPress,
      // added to omit from other
      onSliderDragEnd,
      // added to omit from other
      onSliderDragMove,
      // added to omit from other
      onSliderDragStart,
      // added to omit from other
      onValuesUpdated,
      // added to omit from other
      pitPoints,
      pitPointLabel,
      // added to omit from other
      pitPointSubLabel,
      //#
      snap,
      // added to omit from other
      snapPoints,
      // added to omit from other
      snapOnDragMove,
      // added to omit from other
      values: propValue
    } = _this$props,
        otherProps = _objectWithoutProperties(_this$props, ["algorithm", "className", "data-testing-id", "disabled", "disableSnapOnClick", "handleTooltip", "handleTooltipLabel", "max", "min", "onClick", "onKeyPress", "onSliderDragEnd", "onSliderDragMove", "onSliderDragStart", "onValuesUpdated", "pitPoints", "pitPointLabel", "pitPointSubLabel", "snap", "snapPoints", "snapOnDragMove", "values"]);

    var {
      handlePos,
      values,
      slidingIndex
    } = this.state;
    var renderThumbAndTooltip = handlePos.map((pos, idx) => React.createElement(Handle, {
      role: "slider",
      minValue: this.getMinValue(idx),
      maxValue: this.getMaxValue(idx),
      currentValue: values[idx],
      disabled: disabled,
      "data-handle-key": idx,
      key: "handle-".concat(idx),
      onClick: killEvent,
      onKeyDown: this.handleKeydown,
      onMouseDown: this.startMouseSlide,
      onTouchStart: this.startTouchSlide,
      thumbPosition: pos,
      left: "".concat(pos, "%"),
      showTooltip: handleTooltip && slidingIndex === idx,
      active: typeof slidingIndex === 'number' && slidingIndex === idx,
      "data-testing-id": concatString(testingName, 'slider-handle')
    }, handleTooltip && this.getTooltipLabel(values[idx], idx)));
    var containerClasses = getContainerClasses(pitPoints);
    var sliderClasses = getSliderClasses(disabled);
    var progressBarClasses = getProgressBarClasses(disabled);
    var pitContainerClasses = getPitContainerClasses(disabled);
    var pitSubLabelClasses = getPitSubLabelClasses(disabled);
    return React.createElement("div", otherProps, React.createElement("div", {
      className: containerClasses,
      ref: this.setRef,
      onClick: !disabled ? this.handleClick : null
    }, React.createElement("div", {
      className: sliderClasses,
      "data-testing-id": concatString(testingName, 'slider')
    }), handlePos.map((node, idx, arr) => {
      if (idx === 0 && arr.length > 1) {
        return null;
      }

      var progressLengthAndWidth = this.getProgressStyle(idx);
      return React.createElement("div", {
        className: progressBarClasses,
        key: "progress-bar-".concat(idx),
        "data-testing-id": concatString(testingName, 'slider-progress-bar'),
        style: progressLengthAndWidth
      });
    }), renderThumbAndTooltip, !!pitPoints.length && React.createElement("div", {
      className: pitContainerClasses
    }, pitPoints.map((n, index) => {
      var leftPositionForPitLabel = algorithm.getPosition(n, min, max);
      var pitPointLabelOutsideOfSlider = leftPositionForPitLabel === 100;
      var pitLabelStyles = {
        left: !pitPointLabelOutsideOfSlider ? "".concat(leftPositionForPitLabel, "%") : null,
        right: pitPointLabelOutsideOfSlider ? 0 : null
      };
      return React.createElement("div", {
        className: pitLabelClasses,
        style: pitLabelStyles,
        key: "pit-".concat(n)
      }, this.getPitPointsLabel(n, index));
    }))), pitPointSubLabel && React.createElement("div", {
      className: pitSubLabelClasses
    }, pitPointSubLabel));
  }

}

Rheostat.propTypes = process.env.NODE_ENV !== "production" ? propTypes : {};
Rheostat.defaultProps = defaultProps;
export default Rheostat;